/**
 * UX-PLAN Pillar F — Composer reason channel.
 *
 * The composer used to surface a single boolean ("can submit?") and one
 * string ("Disconnected from gateway."). Four other failure modes
 * (uploading attachment, context full, wrong session, chat guardrail)
 * had zero user-visible signaling. This module is the typed, testable
 * source of truth that replaces both.
 *
 * `computeComposerBlocks` returns a `ComposerBlock[]` — one entry per
 * active gate. The view renders each as a `.callout` at the existing
 * composer slot, reusing the existing design tokens. Adding a new
 * gate means adding a branch here, not editing the view.
 *
 * Pure function. No DOM, no Lit, no settings reads — `connected`,
 * `canSend`, `draft`, `attachments`, `contextUsage`, `wrongSession`
 * are passed in. That makes the branches cheap to unit-test in
 * isolation and keeps the gate logic out of the render path.
 */

export type ComposerBlockKind =
  | "offline"
  | "empty"
  | "uploading"
  | "guardrail"
  | "context_full"
  | "wrong_session";

export type ComposerBlock = {
  kind: ComposerBlockKind;
  message: string;
  recover?: string;
};

/**
 * Percent threshold above which the context chip turns "context full"
 * and the composer adds a `context_full` block. Must stay in sync
 * with `components.css` ring thresholds (>=85 turns the ring red).
 */
export const CONTEXT_FULL_PERCENT = 95;

/**
 * Guardrail max length, mirrored from
 * `src/gateway/server-methods/chat-guardrails.ts` (maxLength default
 * is 32_000). Surfaced here as a typed reason; the UI never edits the
 * server default, just reports it.
 */
export const CHAT_GUARDRAIL_MAX_LENGTH = 32_000;

export type ComposerGateInput = {
  connected: boolean;
  canSend: boolean;
  draft: string;
  hasAttachments: boolean;
  attachmentsUploading: boolean;
  /**
   * Last assistant context usage percent (0..100). When the chat has
   * no completed reply yet, callers should pass `null` so the
   * `context_full` branch stays quiet.
   */
  contextPercent: number | null;
  /**
   * When the current session is not writable from this composer
   * (e.g. a sealed upstream session), pass `true` to surface a
   * `wrong_session` block. The default is `false`.
   */
  wrongSession?: boolean;
  /**
   * Length-guard gate. When the draft exceeds the server max length,
   * a `guardrail` block tells the operator before they hit "Send"
   * and the server rejects it.
   */
  draftLength?: number;
  /**
   * Optional override of the max length used by the guardrail branch.
   * Defaults to {@link CHAT_GUARDRAIL_MAX_LENGTH}.
   */
  guardrailMaxLength?: number;
};

const MESSAGES = {
  offline: "Disconnected from gateway.",
  empty: "Type a message or attach a file to enable Send.",
  uploading: "Attachments are still uploading.",
  guardrail: (max: number) =>
    `Draft exceeds the chat guardrail limit of ${max.toLocaleString()} characters.`,
  contextFull: (percent: number) =>
    `Context is ${percent}% full — start a new session or compact before sending.`,
  wrongSession: "This session is read-only — switch to a writable session before sending.",
} as const;

export function computeComposerBlocks(input: ComposerGateInput): ComposerBlock[] {
  const blocks: ComposerBlock[] = [];

  // `offline` is the only network-level gate. Always first so the
  // operator sees the gateway state before the per-input gates.
  if (!input.connected || !input.canSend) {
    blocks.push({ kind: "offline", message: MESSAGES.offline });
    return blocks;
  }

  // `wrong_session` is the upstream-only gate. Surfaced before
  // per-input gates so a sealed session does not also pile "empty
  // draft" hints on top.
  if (input.wrongSession === true) {
    blocks.push({ kind: "wrong_session", message: MESSAGES.wrongSession });
    return blocks;
  }

  // Per-input gates. Each branch is independent — a draft with no
  // attachments is still "empty", but a draft with an uploading
  // attachment is "uploading" (the more actionable hint).
  const draftText = input.draft.trim();
  const hasPendingPayload = draftText.length > 0 || input.hasAttachments;
  if (!hasPendingPayload) {
    blocks.push({ kind: "empty", message: MESSAGES.empty });
  } else if (input.attachmentsUploading) {
    blocks.push({ kind: "uploading", message: MESSAGES.uploading });
  }

  const max = input.guardrailMaxLength ?? CHAT_GUARDRAIL_MAX_LENGTH;
  const draftLen = input.draftLength ?? draftText.length;
  if (hasPendingPayload && draftLen > max) {
    blocks.push({
      kind: "guardrail",
      message: MESSAGES.guardrail(max),
      recover: "Trim the draft to fit the guardrail limit.",
    });
  }

  if (typeof input.contextPercent === "number" && input.contextPercent >= CONTEXT_FULL_PERCENT) {
    blocks.push({
      kind: "context_full",
      message: MESSAGES.contextFull(input.contextPercent),
      recover: "Open the context panel to compact, or start a new session.",
    });
  }

  return blocks;
}

/**
 * Maps a {@link ComposerBlock} to the matching `.callout` modifier.
 * Mirrors `components.css` (`.callout.danger`, `.callout.warn`,
 * `.callout.info`) so the reason channel inherits the existing
 * design grammar.
 */
export function composerBlockCalloutClass(block: ComposerBlock): "danger" | "warn" | "info" {
  switch (block.kind) {
    case "offline":
    case "guardrail":
    case "context_full":
      return "danger";
    case "wrong_session":
    case "uploading":
      return "warn";
    case "empty":
      return "info";
  }
}
